import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useStoresQuery, useInvalidateStores } from '../../hooks/queries';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Chip,
  Stack,
  Button,
  IconButton,
  Avatar,
  Grid,
  Card,
  CardContent,
  Alert,
  alpha,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  TextField,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  DeviceHub as ChannelsIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeZoneIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  LocalShipping as DeliveryIcon,
  Add as AddIcon,
  Circle as DotIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Store as StoreIcon,
  Language as OnlineIcon,
  Search as SearchIcon,
  Block as BlockIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { Store } from '../../context/StoreManagementContext';
import { DayHoursRow } from '../StoreHours/DayHoursRow';
import { DayHoursRowReadOnly } from '../StoreHours/DayHoursRowReadOnly';
import { formatTime12Hr, formatTime24Hr, partitionSpecialHours } from '../StoreHours/utils';
import { saveHoursForOccasions } from '../StoreHours/saveAllHours';
import { DeliveryAggregator, ChannelSettings } from '../../types/channels';
import { CHANNEL_SNOOZE_PRESETS, DEFAULT_SNOOZE_PRESET, nextFiveAM } from './channelSnoozePresets';
import { format } from 'date-fns';
import {
  OccasionType,
  DayOfWeek,
  DAY_LABELS,
  OCCASION_ORDER,
} from '../StoreHours/types';
import { useTimeFormat } from '../../context/TimeFormatContext';
import { formatSnoozeDateTime } from '../../utils/formatTime';
import { useAuthContext } from '../../context/AuthContext';
import { getStoreChannels, updateStoreOnlineOrders, toggleChannelStatus, updateStoreOccasionHours, addSpecialHours, deleteSpecialHours, deleteSpecialHoursBatch } from '../../api';
import { StoreItemsTab, StoreAuditTab } from './StoreDetail';
import { functionForTabIndex, type StoreFunction } from './storeDetailTabs';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { AddSpecialHoursDialog, SpecialHoursEntry, OccasionType as SpecialHoursOccasionType } from '../StoreHours/AddSpecialHoursDialog';
import { SingleStoreHoursDialog } from '../StoreHours/SingleStoreHoursDialog';
import { DayScheduleV2, SpecialHoursEntryV2, formatOccasionName, OCCASION_HELP, OCCASION_PICKER_HELP } from '../StoreHours/types';
import { OccasionIcon } from '../StoreHours/OccasionIcon';
import { Delete as DeleteIcon, Event as EventIcon } from '@mui/icons-material';
import { HelpTooltip } from '../Onboarding';

interface StoreDetailViewProps {
  store: Store;
  onBack?: () => void;
  onEdit?: (field: string, value: any) => void;
  initialTab?: number;
  onTabChange?: (func: StoreFunction) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`store-tabpanel-${index}`}
      aria-labelledby={`store-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const StoreDetailView: React.FC<StoreDetailViewProps> = ({
  store: storeProp,
  onBack,
  onEdit,
  initialTab = 0,
  onTabChange,
}) => {
  const [currentTab, setCurrentTab] = useState(initialTab);
  const [isEditing, setIsEditing] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get auth context for user info
  const { user } = useAuthContext();

  // Get stores from TanStack Query and invalidation function
  const { data: stores = [] } = useStoresQuery();
  const invalidateStores = useInvalidateStores();

  // Use store from query cache if available (for refresh updates), otherwise use prop
  const store = useMemo(() => {
    const storeFromCache = stores.find(s => s.storeNumber === storeProp.storeNumber);
    return storeFromCache || storeProp;
  }, [stores, storeProp]);

  // Parse hours from store data - returns both regular hours and special hours
  const parseStoreHours = (): { regularHours: any; specialHours: SpecialHoursEntry[] } => {
    // Day names indexed by JavaScript day (0=Sunday, 6=Saturday)
    const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

    // Convert ISO weekday (1-7 where 1=Monday, 7=Sunday) to JS day index (0-6 where 0=Sunday)
    const isoWeekdayToJsDay = (isoDay: number): number => {
      // ISO: 1=Monday, 2=Tuesday, ..., 6=Saturday, 7=Sunday
      // JS:  0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday
      return isoDay === 7 ? 0 : isoDay;
    };

    const defaultHours: any = {
      DELIVERY: { enabled: false, days: {} },
      CARRYOUT: { enabled: false, days: {} },
      DINE_IN: { enabled: false, days: {} },
      DRIVE_THRU: { enabled: false, days: {} }
    };

    // Initialize all days as closed
    Object.keys(defaultHours).forEach(occasionType => {
      dayNames.forEach(dayName => {
        defaultHours[occasionType].days[dayName] = { enabled: false, timeSlots: [] };
      });
    });

    // Collect special hours entries
    const parsedSpecialHours: SpecialHoursEntry[] = [];

    // Temporary structure to collect all blackout entries per date range per occasion
    // Key: "occasionType-startDate-endDate", Value: array of blackout intervals
    const blackoutsByDateRange: Map<string, Array<{
      intervalStart: string;
      duration: number; // in minutes
      occasionType: string;
      startDate: Date;
      endDate: Date;
      dateStrStart: string;
      dateStrEnd: string;
    }>> = new Map();

    // Parse actual hours from store data
    const occasions = store.hours || [];

    occasions.forEach((occasion: any) => {
      const occasionType = occasion.occasion;

      // Skip if not a known occasion type or not enabled
      if (!defaultHours[occasionType] || !occasion.isEnabled) return;

      defaultHours[occasionType].enabled = true;

      // FIRST PASS: Collect all date-bounded blackout entries
      (occasion.hours || []).forEach((hour: any) => {
        const timeFrame = hour.timeFrame || hour;
        const hasDateRange = timeFrame.startDate && timeFrame.endDate;
        const isBlackout = timeFrame.blackout === true;

        if (hasDateRange && isBlackout) {
          const key = `${occasionType}-${timeFrame.startDate}-${timeFrame.endDate}`;
          const startDate = new Date(timeFrame.startDate + 'T00:00:00');
          const endDate = new Date(timeFrame.endDate + 'T00:00:00');
          const intervalStart = timeFrame.intervalStartTime || '00:00';
          const durationStr = timeFrame.duration || '0';

          let duration = 0;
          if (typeof durationStr === 'string' && durationStr.includes(':')) {
            const parts = durationStr.split(':').map(Number);
            if (parts.length >= 2) {
              duration = parts[0] * 60 + parts[1];
            }
          } else {
            duration = Number(durationStr) || 0;
          }

          if (!blackoutsByDateRange.has(key)) {
            blackoutsByDateRange.set(key, []);
          }
          blackoutsByDateRange.get(key)!.push({
            intervalStart,
            duration,
            occasionType,
            startDate,
            endDate,
            dateStrStart: timeFrame.startDate,
            dateStrEnd: timeFrame.endDate,
          });
        }
      });

      // SECOND PASS: Process regular hours (non-date-bounded)
      (occasion.hours || []).forEach((hour: any, hourIndex: number) => {
        // Handle both formats:
        // 1. Backend transformed format: { days, intervalStartTime, duration, ... }
        // 2. Raw Byte Graph format: { timeFrame: { days, intervalStartTime, duration, ... } }
        const timeFrame = hour.timeFrame || hour;

        // Check if this is a special hour entry
        // Special hours have startDate && endDate (regular hours have no date bounds)
        // Note: With simplified format, regular hours no longer have date segmentation
        const hasDateRange = timeFrame.startDate && timeFrame.endDate;
        const isSpecialHours = hasDateRange;

        if (isSpecialHours) {
          // Skip - will be processed from blackoutsByDateRange
          return;
        }

        // For regular hours: show the BASE schedule regardless of date segmentation
        // All segments have the same time definition, just different date bounds
        const days = timeFrame.days;

        if (!days || days.length === 0) {
          return;
        }

        const intervalStart = timeFrame.intervalStartTime || '00:00';
        const durationStr = timeFrame.duration || '0';

        // Parse duration from HH:MM:SS or HH:MM format to minutes
        let duration = 0;
        if (typeof durationStr === 'string' && durationStr.includes(':')) {
          const parts = durationStr.split(':').map(Number);
          if (parts.length >= 2) {
            duration = parts[0] * 60 + parts[1]; // hours * 60 + minutes
          }
        } else {
          duration = Number(durationStr) || 0;
        }

        // Calculate start time in minutes
        const [startHour, startMin] = intervalStart.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;

        // Add duration to get end time
        const endMinutes = startMinutes + duration;

        // Convert back to hours and minutes
        const openHour = startHour;
        const openMin = startMin;
        const closeHour = Math.floor(endMinutes / 60) % 24;
        const closeMin = endMinutes % 60;

        const openStr = `${String(openHour).padStart(2, '0')}:${String(openMin).padStart(2, '0')}`;
        const closeStr = `${String(closeHour).padStart(2, '0')}:${String(closeMin).padStart(2, '0')}`;

        // Apply to each day in this timeframe
        // API returns ISO weekday (1-7), convert to JS day index (0-6)
        (days || []).forEach((isoDayIndex: number) => {
          const jsDayIndex = isoWeekdayToJsDay(isoDayIndex);
          const dayName = dayNames[jsDayIndex];

          if (dayName && defaultHours[occasionType].days[dayName]) {
            defaultHours[occasionType].days[dayName].enabled = true;

            // Deduplicate: only add if this exact time slot doesn't already exist
            // (Segmented hours have same times, just different date bounds)
            const existingSlots = defaultHours[occasionType].days[dayName].timeSlots;
            const alreadyExists = existingSlots.some(
              (slot: { open: string; close: string }) => slot.open === openStr && slot.close === closeStr
            );

            if (!alreadyExists) {
              existingSlots.push({
                open: openStr,
                close: closeStr
              });
            }
          }
        });
      });
    });

    // THIRD PASS: Process collected blackouts to determine closures vs modified hours
    // Modified hours are represented as TWO blackouts:
    //   1. Blackout from 00:00 to openTime (blocks before store opens)
    //   2. Blackout from closeTime to 24:00 (blocks after store closes)
    // Full closures are represented as ONE blackout:
    //   - Blackout from 00:00 with 24:00:00 duration (or 1440 minutes)
    blackoutsByDateRange.forEach((blackouts, key) => {
      if (blackouts.length === 0) return;

      const { occasionType, startDate, endDate: rawEndDate, dateStrStart, dateStrEnd } = blackouts[0];
      const id = `${occasionType}-${dateStrStart}-${dateStrEnd}`;

      // Byte Graph uses EXCLUSIVE endDate (first day the rule no longer applies).
      // Convert to INCLUSIVE endDate (actual last affected day) by subtracting 1 day.
      // Clamp to startDate to handle legacy data where endDate was already inclusive.
      const adjustedEndDate = new Date(rawEndDate);
      adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
      const endDate = adjustedEndDate >= startDate ? adjustedEndDate : rawEndDate;

      // Sort blackouts by intervalStart time to process in order
      blackouts.sort((a, b) => {
        const [aHour, aMin] = a.intervalStart.split(':').map(Number);
        const [bHour, bMin] = b.intervalStart.split(':').map(Number);
        return (aHour * 60 + aMin) - (bHour * 60 + bMin);
      });

      // Check for full closure: single blackout starting at 00:00 with ~24 hour duration (1440 minutes)
      if (blackouts.length === 1) {
        const [startHour, startMin] = blackouts[0].intervalStart.split(':').map(Number);
        const startsAtMidnight = startHour === 0 && startMin === 0;
        const isFullDay = blackouts[0].duration >= 1440; // 24 hours = 1440 minutes

        if (startsAtMidnight && isFullDay) {
          // Full closure
          parsedSpecialHours.push({
            id,
            name: `Special Hours ${format(startDate, 'd MMM')}`,
            startDate,
            endDate,
            isClosed: true,
            openTime: undefined,
            closeTime: undefined,
            occasion: occasionType as OccasionType,
            occasions: [occasionType as OccasionType]
          });
          return;
        }
      }

      // Check for modified hours: TWO blackouts (before open, after close)
      if (blackouts.length === 2) {
        const first = blackouts[0]; // Should be morning blackout (00:00 to openTime)
        const second = blackouts[1]; // Should be evening blackout (closeTime to 24:00)

        const [firstStartHour, firstStartMin] = first.intervalStart.split(':').map(Number);
        const firstStartsAtMidnight = firstStartHour === 0 && firstStartMin === 0;

        if (firstStartsAtMidnight) {
          // The "open window" is between the end of first blackout and start of second blackout
          // openTime = end of first blackout = first.intervalStart + first.duration
          const firstEndMinutes = (firstStartHour * 60 + firstStartMin) + first.duration;
          const openHour = Math.floor(firstEndMinutes / 60) % 24;
          const openMin = firstEndMinutes % 60;

          // closeTime = start of second blackout
          const [secondStartHour, secondStartMin] = second.intervalStart.split(':').map(Number);

          const openStr = `${String(openHour).padStart(2, '0')}:${String(openMin).padStart(2, '0')}`;
          const closeStr = `${String(secondStartHour).padStart(2, '0')}:${String(secondStartMin).padStart(2, '0')}`;

          parsedSpecialHours.push({
            id,
            name: `Special Hours ${format(startDate, 'd MMM')}`,
            startDate,
            endDate,
            isClosed: false,
            openTime: openStr,
            closeTime: closeStr,
            occasion: occasionType as OccasionType,
            occasions: [occasionType as OccasionType]
          });
          return;
        }
      }

      // Fallback: single blackout that doesn't start at midnight or other edge cases
      // Treat as closure to be safe
      parsedSpecialHours.push({
        id,
        name: `Special Hours ${format(startDate, 'd MMM')}`,
        startDate,
        endDate,
        isClosed: true,
        openTime: undefined,
        closeTime: undefined,
        occasion: occasionType as OccasionType,
        occasions: [occasionType as OccasionType]
      });
    });

    // If no hours data, use default mock hours as fallback
    if (occasions.length === 0) {
      defaultHours.DELIVERY.enabled = true;
      defaultHours.CARRYOUT.enabled = true;
      dayNames.slice(1, 6).forEach(dayName => { // Mon-Fri
        defaultHours.DELIVERY.days[dayName] = { enabled: true, timeSlots: [{ open: '09:00', close: '22:00' }] };
        defaultHours.CARRYOUT.days[dayName] = { enabled: true, timeSlots: [{ open: '09:00', close: '22:00' }] };
      });
      dayNames.slice(0, 1).concat(dayNames.slice(6)).forEach(dayName => { // Sat-Sun
        defaultHours.DELIVERY.days[dayName] = { enabled: true, timeSlots: [{ open: '10:00', close: '23:00' }] };
        defaultHours.CARRYOUT.days[dayName] = { enabled: true, timeSlots: [{ open: '10:00', close: '23:00' }] };
      });
    }

    return { regularHours: defaultHours, specialHours: parsedSpecialHours };
  };

  // Parse store hours to get both regular and special hours
  const parsedHoursData = parseStoreHours();
  const [storeHours, setStoreHours] = useState<any>(parsedHoursData.regularHours);


  // Get available occasions from store data - only show occasions the store actually has
  const availableOccasions = useMemo(() => {
    const storeOccasions = (store.hours || []).map((o: any) => o.occasion as OccasionType);
    // Filter OCCASION_ORDER to maintain consistent ordering, only include occasions the store has
    return OCCASION_ORDER.filter(occasion => storeOccasions.includes(occasion));
  }, [store.hours]);

  // Default to the first occasion the store has, falling back to the first configured occasion
  const [selectedOccasion, setSelectedOccasion] = useState<OccasionType>(() => {
    const storeOccasions = (store.hours || []).map((o: any) => o.occasion as OccasionType);
    const firstAvailable = OCCASION_ORDER.find(o => storeOccasions.includes(o));
    return firstAvailable || OCCASION_ORDER[0];
  });
  const { use24HourFormat } = useTimeFormat();

  // Channel management state
  const [channelSettings, setChannelSettings] = useState<ChannelSettings>({
    acceptingOnlineOrders: false,
    appearInStoreSearch: false,
    aggregators: []
  });
  const [originalChannelSettings, setOriginalChannelSettings] = useState<ChannelSettings | null>(null);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelsError, setChannelsError] = useState<string | null>(null);
  // Reactivate-at time staged while editing with Online Orders toggled OFF.
  // Disabling is never indefinite — defaults to the End of Day (5 AM) ceiling.
  const [pendingOoReactivateAt, setPendingOoReactivateAt] = useState<Date | null>(null);
  const [pendingOoQuickOption, setPendingOoQuickOption] = useState<string>('');

  // Save confirmation dialog state
  const [saveConfirmDialog, setSaveConfirmDialog] = useState<{
    open: boolean;
  }>({
    open: false
  });
  const [savingChannels, setSavingChannels] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [hoursError, setHoursError] = useState<string | null>(null);
  const [channelSaveSnackbar, setChannelSaveSnackbar] = useState(false);

  // Special hours state - initialized from parsed data
  const [specialHours, setSpecialHours] = useState<SpecialHoursEntry[]>(parsedHoursData.specialHours);
  // Snapshots captured when entering Store Hours edit mode, used to restore on Cancel
  const [originalStoreHours, setOriginalStoreHours] = useState<any>(null);
  const [originalSpecialHours, setOriginalSpecialHours] = useState<SpecialHoursEntry[] | null>(null);
  const [specialHoursDialogOpen, setSpecialHoursDialogOpen] = useState(false);
  const [specialHoursManagementOpen, setSpecialHoursManagementOpen] = useState(false);
  const [specialHoursDefaultOccasions, setSpecialHoursDefaultOccasions] = useState<SpecialHoursOccasionType[] | undefined>(undefined);

  // Unified hours dialog state
  const [unifiedHoursDialogOpen, setUnifiedHoursDialogOpen] = useState(false);
  const [savingSpecialHours, setSavingSpecialHours] = useState(false);
  const [specialHoursError, setSpecialHoursError] = useState<string | null>(null);
  const [deletingSpecialHoursId, setDeletingSpecialHoursId] = useState<string | null>(null);
  const [specialHoursChanged, setSpecialHoursChanged] = useState(false);
  // Past special hours are hidden by default (they're inert clutter); each
  // surface has its own reveal toggle.
  const [showPastSpecialHours, setShowPastSpecialHours] = useState(false);
  const [showPastInManageAll, setShowPastInManageAll] = useState(false);
  const [clearingPastHours, setClearingPastHours] = useState(false);
  const [clearPastConfirmOpen, setClearPastConfirmOpen] = useState(false);
  const [allHoursChanged, setAllHoursChanged] = useState(false);
  const allHoursChangedRef = useRef(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    entry: SpecialHoursEntry | null;
  }>({ open: false, entry: null });

  // Sync local hours state when store data changes from React Query cache
  // This ensures the UI updates after saving hours and cache invalidation
  useEffect(() => {
    // Don't sync while editing to avoid losing user changes
    if (isEditing || unifiedHoursDialogOpen || specialHoursManagementOpen || specialHoursDialogOpen) {
      return;
    }
    const freshParsedData = parseStoreHours();
    setStoreHours(freshParsedData.regularHours);
    setSpecialHours(freshParsedData.specialHours);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.hours]);

  const currentDay = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase() as DayOfWeek;
  const formatTime = use24HourFormat ? formatTime24Hr : formatTime12Hr;

  // Fetch ByteConnect channel configurations when component mounts or store changes
  useEffect(() => {
    const fetchChannelData = async () => {
      if (!store?.storeNumber) return;

      setChannelsLoading(true);
      setChannelsError(null);

      try {
        const response = await getStoreChannels(store.storeNumber);

        if (response?.status && response?.data) {
          // Map the ByteConnect channels to our aggregators format
          const channels = response.data.channels || [];

          const newSettings = {
            acceptingOnlineOrders: store.onlineOrders ?? false,
            appearInStoreSearch: store.appearInStoreSearch ?? true,
            onlineOrdersReactivateAt: response.data.onlineOrdersReactivateAt
              ? new Date(response.data.onlineOrdersReactivateAt)
              : undefined,
            aggregators: channels.map((channel: any) => {
              // Map channel names to display names
              const getChannelDisplayName = (channelName: string) => {
                const nameMap: { [key: string]: string } = {
                  'UBEREATS': 'Uber Eats',
                  'DOORDASH': 'DoorDash',
                  'GRUBHUB': 'Grubhub',
                  'POSTMATES': 'Postmates',
                  'DELIVEROO': 'Deliveroo',
                  'JUST_EAT': 'Just Eat',
                };
                return nameMap[channelName] || channelName;
              };

              return {
                id: channel.channel,
                name: getChannelDisplayName(channel.channel),
                isActive: channel.status === 'ONLINE',
                isArchived: channel.isArchived || false,
                deactivationReason: channel.deactivationReason,
                deactivationExpiry: channel.deactivationExpiry ? new Date(channel.deactivationExpiry) : undefined,
                deactivatedBy: channel.deactivationBy,
                deactivatedAt: channel.statusUpdatedAt ? new Date(channel.statusUpdatedAt) : undefined,
                markupPercent: channel.markupPercent,
                deliveryProvider: channel.deliveryProvider,
                externalStoreId: channel.externalStoreId
              };
            })
          };
          setChannelSettings(newSettings);
          setOriginalChannelSettings(newSettings);
        }
      } catch (error) {
        console.error('Failed to fetch channel data:', error);
        setChannelsError('Failed to load channel configurations');
      } finally {
        setChannelsLoading(false);
      }
    };

    fetchChannelData();
  }, [store?.storeNumber, store?.onlineOrders, store?.appearInStoreSearch]);

  // Get current store status
  const isStoreOpenNow = useMemo(() => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeMinutes = currentHour * 60 + currentMinute;

    const todayHours = storeHours[selectedOccasion]?.days?.[currentDay];
    if (!todayHours?.enabled) return false;

    return todayHours.timeSlots.some(slot => {
      const [openHour, openMin] = slot.open.split(':').map(Number);
      const [closeHour, closeMin] = slot.close.split(':').map(Number);
      const openMinutes = openHour * 60 + openMin;
      const closeMinutes = closeHour * 60 + closeMin;
      return currentTimeMinutes >= openMinutes && currentTimeMinutes <= closeMinutes;
    });
  }, [storeHours, selectedOccasion, currentDay]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    if (isEditing) {
      if (originalChannelSettings) {
        setChannelSettings(originalChannelSettings);
      }
      if (originalStoreHours) {
        setStoreHours(originalStoreHours);
      }
      if (originalSpecialHours) {
        setSpecialHours(originalSpecialHours);
      }
      setAllHoursChanged(false);
      allHoursChangedRef.current = false;
      setSpecialHoursChanged(false);
    }
    setIsEditing(false); // Reset edit mode when changing tabs
    if (onTabChange) {
      onTabChange(functionForTabIndex(newValue));
    }
  };

  const handleDayToggle = (occasion: OccasionType, day: DayOfWeek, enabled: boolean) => {
    setStoreHours(prev => {
      const currentDayData = prev[occasion]?.days?.[day] || { enabled: false, timeSlots: [] };

      // When enabling a day, add a default time slot if none exist
      const timeSlots = enabled && currentDayData.timeSlots.length === 0
        ? [{ open: '09:00', close: '22:00' }]  // Default hours
        : currentDayData.timeSlots;

      return {
        ...prev,
        [occasion]: {
          ...prev[occasion],
          days: {
            ...prev[occasion].days,
            [day]: { enabled, timeSlots }
          }
        }
      };
    });
  };

  const handleTimeSlotUpdate = (
    occasion: OccasionType,
    day: DayOfWeek,
    slotIndex: number,
    field: 'open' | 'close',
    value: string
  ) => {
    setStoreHours(prev => ({
      ...prev,
      [occasion]: {
        ...prev[occasion],
        days: {
          ...prev[occasion].days,
          [day]: {
            ...prev[occasion].days[day],
            timeSlots: prev[occasion].days[day].timeSlots.map((slot, index) =>
              index === slotIndex ? { ...slot, [field]: value } : slot
            )
          }
        }
      }
    }));
  };

  const handleAddTimeSlot = (occasion: OccasionType, day: DayOfWeek) => {
    setStoreHours(prev => ({
      ...prev,
      [occasion]: {
        ...prev[occasion],
        days: {
          ...prev[occasion].days,
          [day]: {
            ...prev[occasion].days[day],
            timeSlots: [...prev[occasion].days[day].timeSlots, { open: '09:00', close: '17:00' }]
          }
        }
      }
    }));
  };

  const handleRemoveTimeSlot = (occasion: OccasionType, day: DayOfWeek, slotIndex: number) => {
    setStoreHours(prev => ({
      ...prev,
      [occasion]: {
        ...prev[occasion],
        days: {
          ...prev[occasion].days,
          [day]: {
            ...prev[occasion].days[day],
            timeSlots: prev[occasion].days[day].timeSlots.filter((_, index) => index !== slotIndex)
          }
        }
      }
    }));
  };

  // Special hours handlers - auto-save immediately
  const handleAddSpecialHours = async (entry: SpecialHoursEntry) => {
    setSavingSpecialHours(true);
    setSpecialHoursError(null);

    try {
      // Use occasions from dialog, or fall back to selected tab
      const occasionsToSave = entry.occasions?.length > 0
        ? entry.occasions
        : [selectedOccasion];

      // Save for each selected occasion
      let allSuccess = true;
      for (const occasion of occasionsToSave) {
        const response = await addSpecialHours(store.storeNumber, {
          occasion,
          startDate: format(entry.startDate, 'yyyy-MM-dd'),
          endDate: format(entry.endDate, 'yyyy-MM-dd'),
          isClosed: entry.isClosed,
          openTime: entry.openTime,
          closeTime: entry.closeTime
        });

        if (!response?.status) {
          allSuccess = false;
          setSpecialHoursError(response?.message || `Failed to save special hours for ${occasion}`);
          break;
        }

        // Add to local state for each occasion
        const newEntry: SpecialHoursEntry = {
          ...entry,
          id: `special-${Date.now()}-${occasion}`,
          occasion: occasion as any
        };
        setSpecialHours(prev => [...prev, newEntry]);
      }

      if (allSuccess) {
        setSpecialHoursChanged(true);
        setSpecialHoursDialogOpen(false);

        // Trigger refresh to update store data from backend
        invalidateStores();
      }
    } catch (error) {
      console.error('Failed to save special hours:', error);
      setSpecialHoursError(error instanceof Error ? error.message : 'Failed to save special hours');
    } finally {
      setSavingSpecialHours(false);
    }
  };


  // Show confirmation dialog before delete (used in inline view)
  const handleDeleteSpecialHoursClick = (entry: SpecialHoursEntry) => {
    setDeleteConfirmDialog({ open: true, entry });
  };

  // Direct delete without confirmation (used in management dialog)
  // Note: Does NOT call invalidateStores() to avoid closing the modal
  const handleDirectDelete = async (entry: SpecialHoursEntry) => {
    setDeletingSpecialHoursId(entry.id || null);
    setSpecialHoursError(null);

    try {
      // Use format() instead of toISOString() to avoid timezone conversion issues
      // toISOString() converts to UTC which can shift the date for users in timezones ahead of UTC
      const startDateStr = format(entry.startDate, 'yyyy-MM-dd');
      const endDateStr = format(entry.endDate, 'yyyy-MM-dd');

      const response = await deleteSpecialHours(store.storeNumber, {
        occasion: entry.occasion || selectedOccasion,
        startDate: startDateStr,
        endDate: endDateStr
      });

      if (response?.status) {
        setSpecialHours(prev => prev.filter(e => e.id !== entry.id));
        setSpecialHoursChanged(true);
        // Don't invalidateStores() here - it causes modal to close
        // Refresh will happen when modal closes
      } else {
        setSpecialHoursError(response?.message || 'Failed to delete special hours');
      }
    } catch (error) {
      console.error('Failed to delete special hours:', error);
      setSpecialHoursError(error instanceof Error ? error.message : 'Failed to delete special hours');
    } finally {
      setDeletingSpecialHoursId(null);
    }
  };

  // Delete every past special-hours entry in one batched request.
  const handleClearPastSpecialHours = async () => {
    const { past } = partitionSpecialHours(specialHours);
    if (past.length === 0) return;

    setClearPastConfirmOpen(false);
    setClearingPastHours(true);
    setSpecialHoursError(null);

    try {
      const entries = past.map(entry => ({
        occasion: (entry.occasion || selectedOccasion) as OccasionType,
        startDate: format(entry.startDate, 'yyyy-MM-dd'),
        endDate: format(entry.endDate, 'yyyy-MM-dd'),
      }));

      const response = await deleteSpecialHoursBatch(store.storeNumber, entries);

      if (response?.status) {
        const pastIds = new Set(past.map(e => e.id));
        setSpecialHours(prev => prev.filter(e => !pastIds.has(e.id)));
        setSpecialHoursChanged(true);
      } else {
        setSpecialHoursError(response?.message || 'Failed to clear past special hours');
      }
    } catch (error) {
      console.error('Failed to clear past special hours:', error);
      setSpecialHoursError(error instanceof Error ? error.message : 'Failed to clear past special hours');
    } finally {
      setClearingPastHours(false);
    }
  };

  // Actually perform the delete after confirmation
  const handleConfirmDelete = async () => {
    const entry = deleteConfirmDialog.entry;
    if (!entry) return;

    setDeleteConfirmDialog({ open: false, entry: null });
    await handleDirectDelete(entry);
  };

  const handleSaveHours = async () => {
    setSavingHours(true);
    setHoursError(null);

    try {
      const rawDaysData = storeHours[selectedOccasion]?.days || {};

      // Ensure all enabled days have at least one time slot (add default if missing)
      const daysData: Record<string, { enabled: boolean; timeSlots: { open: string; close: string }[] }> = {};
      for (const [day, data] of Object.entries(rawDaysData) as [string, any][]) {
        if (data.enabled && (!data.timeSlots || data.timeSlots.length === 0)) {
          // Add default time slot for enabled days with no slots
          daysData[day] = {
            enabled: true,
            timeSlots: [{ open: '09:00', close: '22:00' }]
          };
        } else {
          daysData[day] = data;
        }
      }

      // Call the API to update store hours for the selected occasion
      const response = await updateStoreOccasionHours(store.storeNumber, {
        occasion: selectedOccasion,
        isEnabled: storeHours[selectedOccasion]?.enabled !== false,
        days: daysData
      });

      if (response?.status) {
        setIsEditing(false);
      } else {
        setHoursError(response?.message || 'Failed to save hours');
      }
    } catch (error) {
      console.error('Failed to save hours:', error);
      setHoursError(error instanceof Error ? error.message : 'Failed to save hours');
    } finally {
      setSavingHours(false);
    }
  };

  // Handler for saving all occasions from unified hours editor
  const handleSaveAllHours = async (data: {
    occasions: Record<OccasionType, {
      enabled: boolean;
      schedule: Record<DayOfWeek, DayScheduleV2>;
    }>;
    specialHours?: Record<OccasionType, SpecialHoursEntryV2[]>;
  }) => {
    setSavingHours(true);
    setHoursError(null);

    try {
      const occasionEntries = Object.entries(data.occasions) as Array<[
        OccasionType,
        typeof data.occasions[OccasionType]
      ]>;

      await saveHoursForOccasions(occasionEntries, async (occasion, occasionData) => {
        const daysData: Record<string, { enabled: boolean; timeSlots: { open: string; close: string }[] }> = {};
        for (const [day, dayData] of Object.entries(occasionData.schedule)) {
          if (dayData.enabled && (!dayData.timeSlots || dayData.timeSlots.length === 0)) {
            daysData[day] = {
              enabled: true,
              timeSlots: [{ open: '09:00', close: '22:00' }]
            };
          } else {
            daysData[day] = {
              enabled: dayData.enabled,
              timeSlots: dayData.timeSlots || []
            };
          }
        }

        await updateStoreOccasionHours(store.storeNumber, {
          occasion,
          isEnabled: occasionData.enabled,
          days: daysData
        });
      });

      // Save special hours if provided - one call per occasion they apply to
      if (data.specialHours) {
        for (const [occasionKey, entries] of Object.entries(data.specialHours)) {
          const occasion = occasionKey as OccasionType;

          // Add each new special hours entry
          for (const entry of entries) {
            // Skip entries that already have an id that looks like it came from the backend
            // (new entries have id like "special-1234567890" from frontend)
            if (entry.id && !entry.id.startsWith('special-')) {
              continue; // Skip existing entries
            }

            await addSpecialHours(store.storeNumber, {
              occasion,
              startDate: entry.startDate,
              endDate: entry.endDate,
              isClosed: entry.isClosed,
              openTime: entry.openTime,
              closeTime: entry.closeTime
            });
          }
        }
      }

      // Mark that changes were made - refresh will happen when dialog closes
      setAllHoursChanged(true);
      allHoursChangedRef.current = true;
    } catch (error) {
      console.error('Failed to save all hours:', error);
      throw error; // Re-throw to let the dialog handle it
    } finally {
      setSavingHours(false);
    }
  };

  const handleSaveChannels = async () => {
    // Show confirmation dialog with summary of all changes
    setSaveConfirmDialog({ open: true });
  };

  const saveChannelChanges = async () => {
    setSavingChannels(true);
    // Whether this save is staging Online Orders OFF (drives scheduling + cascade expiry).
    const turningOnlineOrdersOff = Boolean(
      originalChannelSettings &&
      originalChannelSettings.acceptingOnlineOrders &&
      !channelSettings.acceptingOnlineOrders
    );
    // The server may clamp the requested time (5 AM Europe/London ceiling) — its
    // returned value is authoritative for the cascade expiry and display.
    let ooReactivateAtIso = turningOnlineOrdersOff
      ? (pendingOoReactivateAt ?? DEFAULT_SNOOZE_PRESET.getValue()).toISOString()
      : undefined;

    try {
      // Save online orders toggle if changed
      if (originalChannelSettings && originalChannelSettings.acceptingOnlineOrders !== channelSettings.acceptingOnlineOrders) {
        const ooResponse = await updateStoreOnlineOrders(store.storeNumber, {
          acceptingOnlineOrders: channelSettings.acceptingOnlineOrders,
          reactivateAt: ooReactivateAtIso
        });
        if (turningOnlineOrdersOff && ooResponse?.data?.reactivateAt) {
          ooReactivateAtIso = ooResponse.data.reactivateAt;
        }
      }

      // Process aggregator channel changes
      for (const aggregator of channelSettings.aggregators) {
        const originalAgg = originalChannelSettings?.aggregators.find(a => a.id === aggregator.id);

        if (originalAgg && originalAgg.isActive !== aggregator.isActive) {
          // Only send expiry if it's actually set and is at least 5 minutes in the future
          // This prevents accidental "current time" values from the DateTimePicker
          let expiryToSend: string | undefined = undefined;
          if (!aggregator.isActive && aggregator.pendingDeactivationExpiry) {
            const minValidExpiry = new Date(Date.now() + 5 * 60 * 1000); // 5 min from now
            if (aggregator.pendingDeactivationExpiry > minValidExpiry) {
              expiryToSend = aggregator.pendingDeactivationExpiry.toISOString();
            }
          }

          // Cascade pauses (Online Orders going OFF) inherit the Online Orders
          // reactivate-at time so Byte Graph resumes the aggregators natively
          // when the store turns back on.
          const isCascadePause = turningOnlineOrdersOff && !aggregator.isActive;

          await toggleChannelStatus(
            store.storeNumber,
            aggregator.id,
            aggregator.isActive,
            // Use per-aggregator reason/expiry when deactivating
            !aggregator.isActive
              ? (isCascadePause
                  ? 'Online Orders disabled'
                  : aggregator.pendingDeactivationReason || undefined)
              : undefined,
            isCascadePause ? ooReactivateAtIso : expiryToSend
          );
        }
      }

      // Byte Connect processes channel changes asynchronously — refetching now would
      // return stale "still active" state and stomp the user's just-submitted change.
      // Build an optimistic snapshot from the values we just sent so the UI reflects
      // the new state immediately, and surface a snackbar explaining the async-ness.
      const now = new Date();
      const optimisticSettings: ChannelSettings = {
        ...channelSettings,
        // Turning OFF schedules an auto-reactivation; turning ON cancels it.
        onlineOrdersReactivateAt: turningOnlineOrdersOff && ooReactivateAtIso
          ? new Date(ooReactivateAtIso)
          : channelSettings.acceptingOnlineOrders
          ? undefined
          : channelSettings.onlineOrdersReactivateAt,
        aggregators: channelSettings.aggregators.map((agg) => {
          if (agg.isActive) {
            // Just (re)activated — clear the pause metadata.
            return {
              ...agg,
              deactivationReason: undefined,
              deactivationExpiry: undefined,
              deactivatedBy: undefined,
              deactivatedAt: undefined,
              pendingDeactivationReason: undefined,
              pendingDeactivationExpiry: undefined,
            };
          }
          // Just paused — promote pending fields to live fields.
          return {
            ...agg,
            deactivationReason: agg.pendingDeactivationReason ?? agg.deactivationReason,
            deactivationExpiry: agg.pendingDeactivationExpiry ?? agg.deactivationExpiry,
            deactivatedBy: agg.deactivatedBy ?? user?.email,
            deactivatedAt: agg.deactivatedAt ?? now,
            pendingDeactivationReason: undefined,
            pendingDeactivationExpiry: undefined,
          };
        }),
      };
      setChannelSettings(optimisticSettings);
      setOriginalChannelSettings(optimisticSettings);
      setChannelSaveSnackbar(true);

      setIsEditing(false);
      setSaveConfirmDialog({ open: false });
    } catch (error) {
      console.error('Failed to save channel changes:', error);
      setChannelsError('Failed to save channel changes');
    } finally {
      setSavingChannels(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
      {/* Condensed Header */}
      <Paper
        elevation={0}
        sx={{
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 } }}>
              <IconButton
                onClick={onBack}
                size="small"
                sx={{
                  mt: 0.5,
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>

              <Avatar
                sx={{
                  width: { xs: 36, sm: 44 },
                  height: { xs: 36, sm: 44 },
                  bgcolor: '#662C8B',
                  fontSize: { xs: '0.9rem', sm: '1.125rem' },
                  fontWeight: 'bold',
                }}
              >
                {store.storeNumber?.slice(-2) || 'ST'}
              </Avatar>

              <Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    fontWeight="700" 
                    sx={{ 
                      lineHeight: 1.2,
                      color: 'text.primary',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {store.storeName || `Store ${store.storeNumber}`}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      #{store.storeNumber}
                    </Typography>
                    <Chip
                      label={isStoreOpenNow ? 'Open' : 'Closed'}
                      size="small"
                      color={isStoreOpenNow ? 'success' : 'default'}
                      variant="filled"
                      sx={{ 
                        height: 18, 
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        px: 0.5,
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

          </Box>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationIcon sx={{ fontSize: 14 }} />
              {store.address?.street || store.address?.city
                ? [store.address?.street, store.address?.city, store.address?.state, store.address?.zip].filter(Boolean).join(', ')
                : 'Location not set'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimeZoneIcon sx={{ fontSize: 14 }} />
              {store.timezone || 'America/Chicago'}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 0.5, sm: 2 } }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              fontWeight: 600,
              textTransform: 'none',
              minWidth: { xs: 0, sm: 160 }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          {/* Order, labels and icons mirror the mobile bottom nav — see
              STORE_DETAIL_TABS. Snooze Items leads: it's the most used action. */}
          <Tab icon={<BlockIcon sx={{ fontSize: 18 }} />} label="Snooze Items" iconPosition="start" />
          <Tab icon={<ChannelsIcon sx={{ fontSize: 18 }} />} label="Channels" iconPosition="start" />
          <Tab icon={<ScheduleIcon sx={{ fontSize: 18 }} />} label="Hours" iconPosition="start" />
          <Tab icon={<HistoryIcon sx={{ fontSize: 18 }} />} label="Activity" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Content Area */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto', 
        p: { xs: 2, sm: 3 },
        pb: { xs: 8, sm: 3 } // Space for bottom nav on mobile
      }}>
        {/* Hours — tab 2 (see STORE_DETAIL_TABS) */}
        <TabPanel value={currentTab} index={2}>
          {/* Error Messages */}
          {hoursError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHoursError(null)}>
              {hoursError}
            </Alert>
          )}

          {/* Action Toolbar */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<ScheduleIcon sx={{ fontSize: 16 }} />}
              onClick={() => setUnifiedHoursDialogOpen(true)}
              sx={{
                bgcolor: '#662C8B',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                px: 2,
                '&:hover': {
                  bgcolor: '#551e73'
                }
              }}
            >
              Manage All Hours
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<EventIcon sx={{ fontSize: 16 }} />}
              onClick={() => setSpecialHoursManagementOpen(true)}
              sx={{
                bgcolor: '#0288d1',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                px: 2,
                '&:hover': {
                  bgcolor: '#0277bd'
                }
              }}
            >
              Manage Special Hours
            </Button>
          </Stack>

          {/* Occasions Selector */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 0.5, mb: 1 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', lineHeight: 1.5 }}>
                Occasion
              </Typography>
              <HelpTooltip title={OCCASION_PICKER_HELP} iconSize={16} />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
              {availableOccasions.map((occasion) => (
                <Chip
                  key={occasion}
                  label={formatOccasionName(occasion).toUpperCase()}
                  onClick={() => !isEditing && setSelectedOccasion(occasion)}
                  sx={{
                    cursor: isEditing ? 'default' : 'pointer',
                    bgcolor: selectedOccasion === occasion ? '#662C8B' : 'background.paper',
                    color: selectedOccasion === occasion ? 'white' : 'text.primary',
                    fontWeight: 600,
                    border: 1,
                    borderColor: selectedOccasion === occasion ? '#662C8B' : 'divider',
                    '&:hover': {
                      bgcolor: selectedOccasion === occasion ? '#551e73' : alpha('#662C8B', 0.04),
                    },
                    transition: 'all 0.2s',
                    px: 1,
                    height: 36,
                    '& .MuiChip-icon': {
                      color: selectedOccasion === occasion ? 'inherit' : '#662C8B'
                    }
                  }}
                  icon={<OccasionIcon occasion={occasion} sx={{ fontSize: 18 }} />}
                />
              ))}
              {availableOccasions.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No occasions configured for this store
                </Typography>
              )}
            </Stack>
          </Box>

          {/* Hours Display - only show if there are occasions */}
          {availableOccasions.length > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    {formatOccasionName(selectedOccasion)} Hours
                  </Typography>
                  <HelpTooltip title={OCCASION_HELP[selectedOccasion]} />
                </Stack>
                {isEditing ? (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveHours}
                      disabled={savingHours}
                      sx={{
                        bgcolor: '#662C8B',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 2,
                        '&:hover': {
                          bgcolor: '#551e73'
                        }
                      }}
                    >
                      {savingHours ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        if (originalStoreHours) {
                          setStoreHours(originalStoreHours);
                        }
                        if (originalSpecialHours) {
                          setSpecialHours(originalSpecialHours);
                        }
                        setAllHoursChanged(false);
                        allHoursChangedRef.current = false;
                        setSpecialHoursChanged(false);
                        setIsEditing(false);
                      }}
                      disabled={savingHours}
                      sx={{
                        borderColor: 'divider',
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 2,
                        '&:hover': {
                          borderColor: 'text.secondary',
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      setOriginalStoreHours(storeHours);
                      setOriginalSpecialHours(specialHours);
                      setIsEditing(true);
                    }}
                    sx={{
                      borderColor: 'divider',
                      color: 'text.primary',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 2,
                      px: 2,
                      '&:hover': {
                        borderColor: '#662C8B',
                        bgcolor: alpha('#662C8B', 0.04)
                      }
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Box>

              <Stack spacing={1}>
            {(['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as DayOfWeek[]).map((day) => {
              const dayData = storeHours[selectedOccasion]?.days?.[day] || {
                enabled: true,
                timeSlots: [{ open: '09:00', close: '22:00' }]
              };
              const isToday = day === currentDay;

              if (!isEditing) {
                return (
                  <Paper
                    key={day}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: 1,
                      borderColor: isToday ? '#662C8B' : 'divider',
                      bgcolor: isToday ? alpha('#662C8B', 0.04) : 'background.paper',
                      transition: 'all 0.2s',
                      boxShadow: isToday ? '0 2px 8px rgba(102, 44, 139, 0.1)' : 'none'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography
                          variant="body1"
                          fontWeight={isToday ? 700 : 500}
                          sx={{ 
                            color: isToday ? '#662C8B' : 'text.primary',
                            minWidth: 80
                          }}
                        >
                          {DAY_LABELS[day]}
                        </Typography>
                        {isToday && (
                          <Chip
                            label="TODAY"
                            size="small"
                            sx={{ 
                              height: 20, 
                              fontSize: '0.65rem', 
                              fontWeight: 800,
                              bgcolor: '#662C8B',
                              color: 'white'
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: isToday ? 700 : 400,
                          fontSize: '0.95rem',
                          color: dayData.enabled ? 'text.primary' : 'text.disabled',
                          fontFamily: 'monospace'
                        }}
                      >
                        {dayData.enabled ? (
                          dayData.timeSlots.map((slot, idx) => (
                            <span key={idx}>
                              {idx > 0 && <br />}
                              {formatTime(slot.open)} - {formatTime(slot.close)}
                            </span>
                          ))
                        ) : (
                          <Typography variant="body2" component="span" sx={{ fontStyle: 'italic', opacity: 0.7 }}>
                            Closed
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  </Paper>
                );
              }

              // Edit mode
              return (
                <DayHoursRow
                  key={day}
                  day={day}
                  enabled={dayData.enabled}
                  timeSlots={dayData.timeSlots}
                  onToggleDay={(enabled) => handleDayToggle(selectedOccasion, day, enabled)}
                  onUpdateTimeSlot={(index, field, value) =>
                    handleTimeSlotUpdate(selectedOccasion, day, index, field, value)
                  }
                  onAddTimeSlot={() => handleAddTimeSlot(selectedOccasion, day)}
                  onRemoveTimeSlot={(index) => handleRemoveTimeSlot(selectedOccasion, day, index)}
                  isToday={isToday}
                  use24Hour={use24HourFormat}
                />
              );
            })}
          </Stack>

          {/* Special Hours Section */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon sx={{ color: 'text.secondary' }} />
                  <Typography variant="h6">Special Hours</Typography>
                  <Chip
                    label={formatOccasionName(selectedOccasion)}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      bgcolor: alpha('#662C8B', 0.1),
                      color: '#662C8B'
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  onClick={() => setSpecialHoursManagementOpen(true)}
                  sx={{
                    color: '#662C8B',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Manage all →
                </Typography>
              </Box>

              {/* Error Messages */}
              {specialHoursError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSpecialHoursError(null)}>
                  {specialHoursError}
                </Alert>
              )}

              {/* Filter special hours by the selected occasion; past entries
                  are collapsed behind a toggle so upcoming ones stay visible. */}
              {(() => {
                const occasionEntries = specialHours.filter(sh => sh.occasion === selectedOccasion);
                const { current: currentEntries, past: pastEntries } = partitionSpecialHours(occasionEntries);

                const renderEntry = (entry: SpecialHoursEntry) => {
                      // Compare dates only (not time) - endDate is inclusive (end of day)
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const endDateOnly = new Date(entry.endDate);
                      endDateOnly.setHours(23, 59, 59, 999);
                      const startDateOnly = new Date(entry.startDate);
                      startDateOnly.setHours(0, 0, 0, 0);
                      const isPast = endDateOnly < today;
                      const isActive = startDateOnly <= today && endDateOnly >= today;

                      return (
                        <Paper
                          key={entry.id}
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: 1,
                            borderColor: isActive ? '#662C8B' : isPast ? 'grey.300' : 'divider',
                            bgcolor: isActive ? alpha('#662C8B', 0.04) : isPast ? 'grey.50' : 'background.paper',
                            opacity: isPast ? 0.6 : 1,
                            transition: 'all 0.2s'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="subtitle1" fontWeight={600}>
                                    {format(entry.startDate, 'd MMM yyyy')}
                                    {entry.endDate.getTime() !== entry.startDate.getTime() && (
                                      <> — {format(entry.endDate, 'd MMM yyyy')}</>
                                    )}
                                  </Typography>
                                  {isActive && (
                                    <Chip
                                      label="ACTIVE"
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        bgcolor: '#662C8B',
                                        color: 'white'
                                      }}
                                    />
                                  )}
                                  {isPast && (
                                    <Chip
                                      label="PAST"
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.65rem' }}
                                    />
                                  )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {format(entry.startDate, 'd MMM yyyy')}
                                  {entry.endDate.getTime() !== entry.startDate.getTime() && (
                                    <> — {format(entry.endDate, 'd MMM yyyy')}</>
                                  )}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 500,
                                  color: entry.isClosed ? 'error.main' : 'text.primary',
                                  fontFamily: entry.isClosed ? 'inherit' : 'monospace'
                                }}
                              >
                                {entry.isClosed ? 'Closed' : `${formatTime(entry.openTime || '')} - ${formatTime(entry.closeTime || '')}`}
                              </Typography>

                              <Stack direction="row" spacing={0.5}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteSpecialHoursClick(entry)}
                                  sx={{ color: 'error.main' }}
                                  disabled={deletingSpecialHoursId === entry.id}
                                >
                                  {deletingSpecialHoursId === entry.id ? (
                                    <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Box sx={{
                                        width: 14,
                                        height: 14,
                                        border: '2px solid',
                                        borderColor: 'error.main',
                                        borderTopColor: 'transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        '@keyframes spin': {
                                          '0%': { transform: 'rotate(0deg)' },
                                          '100%': { transform: 'rotate(360deg)' }
                                        }
                                      }} />
                                    </Box>
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Stack>
                            </Box>
                          </Box>
                        </Paper>
                      );
                    };

                return (
                  <>
                    {currentEntries.length === 0 ? (
                      <Box sx={{
                        py: 4,
                        textAlign: 'center',
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px dashed',
                        borderColor: 'divider'
                      }}>
                        <EventIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          No upcoming special hours for {formatOccasionName(selectedOccasion).toLowerCase()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Add holiday hours, temporary closures, or modified schedules
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={1.5}>{currentEntries.map(renderEntry)}</Stack>
                    )}
                    {pastEntries.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Button
                          size="small"
                          onClick={() => setShowPastSpecialHours(v => !v)}
                          sx={{ color: 'text.secondary', textTransform: 'none' }}
                        >
                          {showPastSpecialHours ? 'Hide past' : `Show past (${pastEntries.length})`}
                        </Button>
                        {showPastSpecialHours && (
                          <Stack spacing={1.5} sx={{ mt: 1 }}>{pastEntries.map(renderEntry)}</Stack>
                        )}
                      </Box>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
            </>
          )}

          {/* Special Hours Dialog */}
          <AddSpecialHoursDialog
            open={specialHoursDialogOpen}
            onClose={() => {
              setSpecialHoursDialogOpen(false);
              setSpecialHoursDefaultOccasions(undefined);
            }}
            onSave={handleAddSpecialHours}
            use24Hour={use24HourFormat}
            saving={savingSpecialHours}
            availableOccasions={availableOccasions as SpecialHoursOccasionType[]}
            defaultOccasions={specialHoursDefaultOccasions}
          />

          {/* Special Hours Management Dialog */}
          <Dialog
            open={specialHoursManagementOpen}
            onClose={() => {
              setSpecialHoursManagementOpen(false);
              // Only refresh if changes were made
              if (specialHoursChanged) {
                invalidateStores();
                setSpecialHoursChanged(false);
              }
            }}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { maxHeight: '80vh' } }}
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon sx={{ color: '#662C8B' }} />
                  <Typography variant="h6">Manage Special Hours</Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSpecialHoursDefaultOccasions(undefined); // All occasions by default
                    setSpecialHoursDialogOpen(true);
                  }}
                  sx={{
                    bgcolor: '#662C8B',
                    '&:hover': { bgcolor: '#551e73' }
                  }}
                >
                  Add Special Hours
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {store.storeName || `Store ${store.storeNumber}`}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {specialHoursError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSpecialHoursError(null)}>
                  {specialHoursError}
                </Alert>
              )}

              {(() => {
                const { current: currentEntries, past: pastEntries } = partitionSpecialHours(specialHours);

                const renderEntry = (entry: SpecialHoursEntry) => {
                      const isPast = entry.endDate < new Date();
                      const isActive = entry.startDate <= new Date() && entry.endDate >= new Date();
                      const isDeleting = deletingSpecialHoursId === entry.id;

                      return (
                        <Paper
                          key={entry.id}
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: 1,
                            borderColor: isActive ? '#662C8B' : isPast ? 'grey.300' : 'divider',
                            bgcolor: isActive ? alpha('#662C8B', 0.04) : isPast ? 'grey.50' : 'background.paper',
                            opacity: isPast ? 0.6 : 1
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {format(entry.startDate, 'd MMM yyyy')}
                                  {entry.startDate.getTime() !== entry.endDate.getTime() && (
                                    <> — {format(entry.endDate, 'd MMM yyyy')}</>
                                  )}
                                </Typography>
                                {isActive && (
                                  <Chip label="ACTIVE" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#662C8B', color: 'white' }} />
                                )}
                                {isPast && (
                                  <Chip label="PAST" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                <Chip
                                  label={entry.isClosed ? 'Closed' : `${entry.openTime} - ${entry.closeTime}`}
                                  size="small"
                                  color={entry.isClosed ? 'error' : 'default'}
                                  variant={entry.isClosed ? 'filled' : 'outlined'}
                                  sx={{ height: 24 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {entry.occasion ? formatOccasionName(entry.occasion) : 'All occasions'}
                                </Typography>
                              </Box>
                            </Box>
                            <Stack direction="row" spacing={0.5}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDirectDelete(entry)}
                                disabled={isDeleting}
                                title="Delete special hours"
                              >
                                {isDeleting ? (
                                  <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ width: 16, height: 16, border: 2, borderColor: 'error.main', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                                  </Box>
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Stack>
                          </Box>
                        </Paper>
                      );
                    };

                const hasNoEntries = currentEntries.length === 0 && pastEntries.length === 0;
                const renderCurrentSection = () => {
                  if (hasNoEntries) {
                    return (
                      <Box sx={{
                        py: 6,
                        textAlign: 'center',
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px dashed',
                        borderColor: 'divider'
                      }}>
                        <EventIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                          No special hours configured
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Add holiday hours, temporary closures, or modified schedules
                        </Typography>
                      </Box>
                    );
                  }
                  if (currentEntries.length === 0) {
                    return (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No upcoming special hours
                      </Typography>
                    );
                  }
                  return <Stack spacing={1.5}>{currentEntries.map(renderEntry)}</Stack>;
                };

                return (
                  <>
                    {renderCurrentSection()}

                    {pastEntries.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Button
                            size="small"
                            onClick={() => setShowPastInManageAll(v => !v)}
                            sx={{ color: 'text.secondary', textTransform: 'none' }}
                          >
                            {showPastInManageAll ? 'Hide past' : `Show past (${pastEntries.length})`}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={clearingPastHours}
                            onClick={() => setClearPastConfirmOpen(true)}
                          >
                            {clearingPastHours ? 'Clearing…' : `Clear past (${pastEntries.length})`}
                          </Button>
                        </Box>
                        {showPastInManageAll && (
                          <Stack spacing={1.5} sx={{ mt: 1 }}>{pastEntries.map(renderEntry)}</Stack>
                        )}
                      </Box>
                    )}
                  </>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setSpecialHoursManagementOpen(false);
                // Only refresh if changes were made
                if (specialHoursChanged) {
                  invalidateStores();
                  setSpecialHoursChanged(false);
                }
              }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Clear-past confirmation */}
          <Dialog open={clearPastConfirmOpen} onClose={() => setClearPastConfirmOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Clear past special hours?</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                This permanently removes {partitionSpecialHours(specialHours).past.length} past special-hours
                {' '}{partitionSpecialHours(specialHours).past.length === 1 ? 'entry' : 'entries'} from this store.
                Upcoming and active entries are not affected.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setClearPastConfirmOpen(false)}>Cancel</Button>
              <Button color="error" variant="contained" onClick={handleClearPastSpecialHours}>
                Clear past
              </Button>
            </DialogActions>
          </Dialog>

          {/* Unified Hours Dialog - Edit All Occasions */}
          <SingleStoreHoursDialog
            open={unifiedHoursDialogOpen}
            onClose={() => {
              setUnifiedHoursDialogOpen(false);
              // Only refresh if changes were made (use ref to avoid stale closure)
              if (allHoursChangedRef.current) {
                invalidateStores();
                setAllHoursChanged(false);
                allHoursChangedRef.current = false;
              }
            }}
            storeNumber={store.storeNumber}
            storeName={store.storeName || `Store ${store.storeNumber}`}
            availableOccasions={availableOccasions}
            existingHours={(() => {
              // Convert storeHours to the format expected by UnifiedHoursEditor
              const result: Record<OccasionType, { enabled: boolean; schedule: Record<DayOfWeek, DayScheduleV2> }> = {} as any;
              availableOccasions.forEach(occasion => {
                const occasionData = storeHours[occasion];
                const schedule: Record<DayOfWeek, DayScheduleV2> = {} as any;
                (['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as DayOfWeek[]).forEach(day => {
                  const dayData = occasionData?.days?.[day];
                  schedule[day] = {
                    enabled: dayData?.enabled ?? false,
                    timeSlots: dayData?.timeSlots ?? []
                  };
                });
                result[occasion] = {
                  enabled: occasionData?.enabled ?? false,
                  schedule
                };
              });
              return result;
            })()}
            onSave={handleSaveAllHours}
          />

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteConfirmDialog.open}
            onClose={() => setDeleteConfirmDialog({ open: false, entry: null })}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ pb: 1 }}>
              Delete Special Hours?
            </DialogTitle>
            <DialogContent>
              {deleteConfirmDialog.entry && (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    Are you sure you want to delete this special hours entry?
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {format(deleteConfirmDialog.entry.startDate, 'd MMM yyyy')}
                      {deleteConfirmDialog.entry.endDate.getTime() !== deleteConfirmDialog.entry.startDate.getTime() && (
                        <> — {format(deleteConfirmDialog.entry.endDate, 'd MMM yyyy')}</>
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: deleteConfirmDialog.entry.isClosed ? 'error.main' : 'text.primary'
                      }}
                    >
                      {deleteConfirmDialog.entry.isClosed
                        ? 'Closed'
                        : `${deleteConfirmDialog.entry.openTime} - ${deleteConfirmDialog.entry.closeTime}`
                      }
                    </Typography>
                  </Paper>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    This action cannot be undone.
                  </Alert>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteConfirmDialog({ open: false, entry: null })}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                variant="contained"
                color="error"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          {/* Manage Channels Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            {isEditing ? (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveChannels}
                  disabled={savingChannels}
                  sx={{
                    bgcolor: '#662C8B',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2,
                    '&:hover': {
                      bgcolor: '#551e73'
                    }
                  }}
                >
                  {savingChannels ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (originalChannelSettings) {
                      setChannelSettings(originalChannelSettings);
                    }
                    setIsEditing(false);
                  }}
                  disabled={savingChannels}
                  sx={{
                    borderColor: 'divider',
                    color: 'text.secondary',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2,
                    '&:hover': {
                      borderColor: 'text.secondary',
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            ) : (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  // Seed pending fields from the saved state so an already-paused
                  // channel shows its existing reason + reactivation time when editing
                  // (instead of appearing blank, which is confusing and risks the
                  // values looking wiped while managing a different channel).
                  setChannelSettings(prev => ({
                    ...prev,
                    aggregators: prev.aggregators.map(agg =>
                      agg.isActive
                        ? agg
                        : {
                            ...agg,
                            pendingDeactivationReason:
                              agg.pendingDeactivationReason ?? agg.deactivationReason,
                            pendingDeactivationExpiry:
                              agg.pendingDeactivationExpiry ?? agg.deactivationExpiry,
                          }
                    ),
                  }));
                  setIsEditing(true);
                }}
                sx={{
                  borderColor: 'divider',
                  color: 'text.primary',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 2,
                  '&:hover': {
                    borderColor: '#662C8B',
                    bgcolor: alpha('#662C8B', 0.04)
                  }
                }}
              >
                Manage Channels
              </Button>
            )}
          </Box>

          {channelsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography color="text.secondary">Loading channel configurations...</Typography>
            </Box>
          ) : channelsError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {channelsError}
            </Alert>
          ) : (
            <Stack spacing={3}>
              {/* Online Orders Section */}
              <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OnlineIcon />
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="h6">
                          Online Orders
                        </Typography>
                        <HelpTooltip title="Controls ALL online orders, including website, mobile app, and delivery aggregator ordering. To disable ONLY delivery aggregators, keep this toggle enabled, and use the individual toggles below." />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Website, mobile app, and delivery aggregator ordering
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {channelSettings.acceptingOnlineOrders ? (
                      <Chip label="Active" color="success" />
                    ) : (
                      <Chip label="Inactive" color="default" variant="outlined" />
                    )}
                    <Switch
                      checked={channelSettings.acceptingOnlineOrders}
                      onChange={(e) => {
                        if (!isEditing) return;
                        const nextAcceptingOnlineOrders = e.target.checked;
                        // Disabling always carries a reactivate-at time — default to
                        // 1H (same as aggregator pauses); clear it when re-enabling.
                        if (nextAcceptingOnlineOrders) {
                          setPendingOoReactivateAt(null);
                          setPendingOoQuickOption('');
                        } else {
                          setPendingOoReactivateAt(DEFAULT_SNOOZE_PRESET.getValue());
                          setPendingOoQuickOption(DEFAULT_SNOOZE_PRESET.key);
                        }
                        setChannelSettings(prev => ({
                          ...prev,
                          acceptingOnlineOrders: nextAcceptingOnlineOrders,
                          // Turning Online Orders OFF force-pauses every aggregator and
                          // clears any pending reason/expiry — the cascade pauses inherit
                          // the Online Orders reactivate-at time at save so everything
                          // comes back together, and we don't want per-aggregator values
                          // leaking into the Save payload or the confirmation summary.
                          aggregators: nextAcceptingOnlineOrders
                            ? prev.aggregators
                            : prev.aggregators.map(agg => ({
                                ...agg,
                                isActive: false,
                                pendingDeactivationReason: undefined,
                                pendingDeactivationExpiry: undefined,
                                pendingQuickOption: undefined,
                              })),
                        }));
                      }}
                      disabled={!isEditing}
                      color="primary"
                    />
                  </Box>
                </Box>

                {/* Pending auto-reactivation (view mode) */}
                {!isEditing && !channelSettings.acceptingOnlineOrders &&
                  channelSettings.onlineOrdersReactivateAt && (
                  <Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
                    <Typography variant="body2">
                      Online Orders will automatically turn back ON at{' '}
                      {formatSnoozeDateTime(channelSettings.onlineOrdersReactivateAt, use24HourFormat)}.
                    </Typography>
                  </Alert>
                )}

                {/* Reactivate-at picker when staging Online Orders OFF (edit mode) */}
                {isEditing && !channelSettings.acceptingOnlineOrders &&
                  originalChannelSettings?.acceptingOnlineOrders && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Turn back on after
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1, mb: 1.5 }}>
                      {CHANNEL_SNOOZE_PRESETS.map((preset) => (
                        <Chip
                          key={preset.key}
                          label={preset.label}
                          onClick={() => {
                            setPendingOoReactivateAt(preset.getValue());
                            setPendingOoQuickOption(preset.key);
                          }}
                          variant={pendingOoQuickOption === preset.key ? 'filled' : 'outlined'}
                          color={pendingOoQuickOption === preset.key ? 'primary' : 'default'}
                          size="small"
                        />
                      ))}
                    </Stack>
                    {/* Preset chips only — no free date/time entry, so the expiry
                        can never land past the 5 AM ceiling. */}
                    {pendingOoReactivateAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Online Orders (and the paused aggregators) will automatically turn back ON at{' '}
                        {formatSnoozeDateTime(pendingOoReactivateAt, use24HourFormat)}.
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Delivery Aggregators Section */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeliveryIcon />
                  Delivery Aggregators
                  <HelpTooltip title="Toggle individual delivery platforms on/off. When pausing, set an expiration time for automatic reactivation." />
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Alert severity="info" sx={{ mb: 2 }}>
                  Delivery aggregator changes are processed asynchronously and may take a few minutes to reflect.
                </Alert>

                <List sx={{ p: 0 }}>
                  {channelSettings.aggregators.map((aggregator, index) => (
                    <React.Fragment key={aggregator.id}>
                      {index > 0 && <Divider />}
                      <ListItem sx={{ px: 0, py: 2, display: 'block' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <ListItemIcon>
                            {aggregator.isActive ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <BlockIcon color="error" />
                            )}
                          </ListItemIcon>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" fontWeight={500}>
                                {aggregator.name}
                              </Typography>
                              <Chip
                                label={aggregator.isActive ? 'Active' : 'Inactive'}
                                size="small"
                                color={aggregator.isActive ? 'success' : 'error'}
                                variant={aggregator.isActive ? 'filled' : 'outlined'}
                              />
                            </Box>
                            {/* Show current status when not editing */}
                            {!isEditing && aggregator.deactivationReason && (
                              <Box sx={{ mt: 1 }}>
                                <Alert severity="warning" sx={{ py: 0.5 }}>
                                  <Typography variant="body2">
                                    {aggregator.deactivationReason}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Deactivated by {aggregator.deactivatedBy} on{' '}
                                    {aggregator.deactivatedAt && formatSnoozeDateTime(aggregator.deactivatedAt, use24HourFormat)}
                                    {aggregator.deactivationExpiry && (
                                      <> • Reactivates: {formatSnoozeDateTime(aggregator.deactivationExpiry, use24HourFormat)}</>
                                    )}
                                  </Typography>
                                </Alert>
                              </Box>
                            )}
                            {!isEditing && !aggregator.deactivationReason && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Integration is active and accepting orders
                              </Typography>
                            )}

                            {/* Show inline fields when editing and toggled OFF - Compact design.
                                Hide them entirely when Online Orders is OFF: per-aggregator
                                reactivation is moot in that case (the backend won't honor it)
                                and showing the picker misled testers. */}
                            {isEditing && !aggregator.isActive && channelSettings.acceptingOnlineOrders && (
                              <Box sx={{ mt: 1.5, pl: 5, pr: 6 }}>
                                {/* Preset chips only for the expiry — no free date/time
                                    entry, so it can never land past the 5 AM ceiling. */}
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      label="Reason (optional)"
                                      size="small"
                                      fullWidth
                                      value={aggregator.pendingDeactivationReason || ''}
                                      onChange={(e) => {
                                        const updatedAggregators = [...channelSettings.aggregators];
                                        updatedAggregators[index] = {
                                          ...aggregator,
                                          pendingDeactivationReason: e.target.value
                                        };
                                        setChannelSettings(prev => ({
                                          ...prev,
                                          aggregators: updatedAggregators
                                        }));
                                      }}
                                      placeholder="e.g., Kitchen busy"
                                    />
                                  </Grid>
                                </Grid>

                                {/* Quick Options */}
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption" color="text.secondary" gutterBottom>
                                    Quick Options
                                  </Typography>
                                  <Stack spacing={1} sx={{ mt: 1 }}>
                                    {/* Short-duration options */}
                                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                      {CHANNEL_SNOOZE_PRESETS.map((preset) => {
                                        const isSelected = aggregator.pendingQuickOption === preset.key;
                                        return (
                                          <Chip
                                            key={preset.key}
                                            label={preset.label}
                                            onClick={() => {
                                              const expiry = preset.getValue();
                                              const updatedAggregators = [...channelSettings.aggregators];
                                              updatedAggregators[index] = {
                                                ...aggregator,
                                                pendingDeactivationExpiry: expiry,
                                                pendingQuickOption: preset.key,
                                              };
                                              setChannelSettings(prev => ({
                                                ...prev,
                                                aggregators: updatedAggregators
                                              }));
                                            }}
                                            variant={isSelected ? 'filled' : 'outlined'}
                                            color={isSelected ? 'primary' : 'default'}
                                            size="small"
                                          />
                                        );
                                      })}
                                    </Stack>
                                  </Stack>
                                </Box>

                                {aggregator.pendingDeactivationExpiry && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Channel will reactivate: {formatSnoozeDateTime(aggregator.pendingDeactivationExpiry, use24HourFormat)}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                          {isEditing && (
                            <Switch
                              checked={aggregator.isActive}
                              disabled={!channelSettings.acceptingOnlineOrders}
                              onChange={(e) => {
                                const isNowActive = e.target.checked;
                                let newExpiry = isNowActive ? undefined : aggregator.pendingDeactivationExpiry;
                                let newQuickOption = isNowActive ? undefined : aggregator.pendingQuickOption;
                                // Default to 1H when toggling off without a prior expiry selection
                                if (!isNowActive && !newExpiry) {
                                  newExpiry = DEFAULT_SNOOZE_PRESET.getValue();
                                  newQuickOption = DEFAULT_SNOOZE_PRESET.key;
                                }
                                const updatedAggregators = [...channelSettings.aggregators];
                                updatedAggregators[index] = {
                                  ...aggregator,
                                  isActive: isNowActive,
                                  pendingDeactivationReason: isNowActive ? undefined : aggregator.pendingDeactivationReason,
                                  pendingDeactivationExpiry: newExpiry,
                                  pendingQuickOption: newQuickOption,
                                };
                                setChannelSettings(prev => ({
                                  ...prev,
                                  aggregators: updatedAggregators
                                }));
                              }}
                            />
                          )}
                        </Box>
                      </ListItem>
                    </React.Fragment>
                  ))}

                  {channelSettings.aggregators.length === 0 && (
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No delivery aggregators configured for this store
                      </Typography>
                    </Box>
                  )}
                </List>
              </CardContent>
            </Card>
            </Stack>
          )}
        </TabPanel>

        {/* Snooze Items — tab 0 */}
        <TabPanel value={currentTab} index={0}>
          <StoreItemsTab storeNumber={store.storeNumber} />
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <StoreAuditTab storeNumber={store.storeNumber} />
        </TabPanel>
      </Box>

      {/* Save Confirmation Dialog */}
      <Dialog
        open={saveConfirmDialog.open}
        onClose={() => setSaveConfirmDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Channel Changes
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Summary of Changes:
            </Typography>

            {/* Online Orders Change */}
            {originalChannelSettings &&
             originalChannelSettings.acceptingOnlineOrders !== channelSettings.acceptingOnlineOrders && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Online Orders:</strong> {channelSettings.acceptingOnlineOrders ? 'Turning ON' : 'Turning OFF'}
                </Typography>
                {!channelSettings.acceptingOnlineOrders && pendingOoReactivateAt && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Automatically turns back ON at{' '}
                    {formatSnoozeDateTime(pendingOoReactivateAt, use24HourFormat)}
                  </Typography>
                )}
              </Box>
            )}

            {/* Aggregator Changes */}
            {(() => {
              const activations = channelSettings.aggregators.filter((agg) => {
                const originalAgg = originalChannelSettings?.aggregators.find(a => a.id === agg.id);
                return originalAgg && !originalAgg.isActive && agg.isActive;
              });

              const deactivations = channelSettings.aggregators.filter((agg) => {
                const originalAgg = originalChannelSettings?.aggregators.find(a => a.id === agg.id);
                return originalAgg && originalAgg.isActive && !agg.isActive;
              });

              return (
                <>
                  {activations.length > 0 && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: 'success.50', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Activating:</strong>
                      </Typography>
                      {activations.map(agg => (
                        <Chip
                          key={agg.id}
                          label={agg.name}
                          size="small"
                          color="success"
                          sx={{ mr: 1, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  )}

                  {deactivations.length > 0 && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: 'error.50', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Deactivating:</strong>
                      </Typography>
                      {deactivations.map(agg => (
                        <Box key={agg.id} sx={{ mb: 1 }}>
                          <Chip
                            label={agg.name}
                            size="small"
                            color="error"
                            sx={{ mb: 0.5 }}
                          />
                          {agg.pendingDeactivationReason && (
                            <Typography variant="caption" display="block" sx={{ ml: 1 }}>
                              Reason: {agg.pendingDeactivationReason}
                            </Typography>
                          )}
                          {agg.pendingDeactivationExpiry && (
                            <Typography variant="caption" display="block" sx={{ ml: 1 }}>
                              Reactivates: {formatSnoozeDateTime(agg.pendingDeactivationExpiry, use24HourFormat)}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              );
            })()}

            {/* Warning for deactivations */}
            {channelSettings.aggregators.some(agg => {
              const originalAgg = originalChannelSettings?.aggregators.find(a => a.id === agg.id);
              return originalAgg && originalAgg.isActive && !agg.isActive;
            }) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Deactivating channels will immediately stop accepting orders from these platforms.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSaveConfirmDialog({ open: false })}
            disabled={savingChannels}
          >
            Cancel
          </Button>
          <Button
            onClick={saveChannelChanges}
            variant="contained"
            color="primary"
            disabled={savingChannels}
          >
            {savingChannels ? 'Saving...' : 'Confirm Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={channelSaveSnackbar}
        autoHideDuration={6000}
        onClose={() => setChannelSaveSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setChannelSaveSnackbar(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Channel changes submitted. Byte Connect can take a couple of minutes to fully propagate.
        </Alert>
      </Snackbar>
    </Box>
  );
};